import * as React from 'react'
import { ILeaf, INode } from 'shared/types/tree'
import {
  allUrlsInTree,
  getGradeId,
  getSubjectId,
  nodeAttribution,
  nodesAndLeavesWithContent,
} from '../utility/TreeHelpers'
import { NodeIdMap } from '../version_mapping/utility'
import {
  idsForNode,
  updateAll,
  UpdateMethod,
  UpdateType,
} from '../tree/nodeUpdates/utility'
import { parseHtmlString } from 'shared/utils'
import {
  isExternalUrl,
  subjectForCode,
  uploadContentFiles,
} from '../ingestion/Utility'
import { filterNodeForParams } from 'shared/utils'
import { replaceParamsForUpdate } from 'shared/utils/contentTransforms'
import { nanoid } from 'nanoid'
import mime from 'mime'
import { doInBatches } from '../../utility'
import { GET } from 'infrastructure/api'
import { NodeBasedUpdates } from '../tree/nodeUpdates/types'

export enum ImageStatus {
  Valid,
  Invalid,
  Data,
  Insecure,
  Blocked,
  ImproperFormat,
  FailedMathFormat,
  SVGTypeMismatch,
}

export const fileName = (url: string) => url.split('/').pop()!

const isMalformedMathURL = (url: string) => url.startsWith('blob:http')

const isDataUrl = (url: string) =>
  url.startsWith('data:') || url.startsWith('blob:')

const dataImageType = (src: string) => {
  const mimeType: string = src.split(';')[0].split(':')[1]
  return mime.getExtension(mimeType) ?? ''
}

export async function imageUrlStatus(url: string): Promise<ImageStatus> {
  if (isDataUrl(url)) return ImageStatus.Data
  if (url.includes('http:')) return ImageStatus.Insecure
  if (/^((\.{1,2}\/)+|\.{3,})(https?:\/\/)/.test(url)) {
    return ImageStatus.ImproperFormat
  }
  const options = { method: 'GET' }
  try {
    const response = await fetch(url, options)
    const data = await response.text()
    if (data.includes('<svg ') && !url.includes('.svg')) {
      return ImageStatus.SVGTypeMismatch
    }
    return response.headers.get('Content-Type')?.startsWith('image')
      ? ImageStatus.Valid
      : ImageStatus.Invalid
  } catch {
    return ImageStatus.Blocked
  }
}

type treeImageContext = {
  tree: INode
  idMap: NodeIdMap
  imageImportScript: (...urls: string[]) => void
}

export const TreeImageContext = React.createContext<treeImageContext>({
  tree: {} as INode,
  imageImportScript: () => {},
  idMap: {},
})

function sanitizeContent(content: string) {
  if (!content.toLowerCase().includes('onerror=')) return content
  const asHtml = parseHtmlString(content)
  Array.from(asHtml.getElementsByTagName('img')).forEach((elm) => {
    elm.removeAttribute('onerror')
  })
  return asHtml.body.innerHTML
}

export type ReplacementData = {
  existing: string
  replaceWith: string
}

export type NodeContentUpdate = {
  node: INode
  replacements: ReplacementData[]
}

type LeafContentUpdate = {
  leaf: ILeaf
  replacements: ReplacementData[]
}

export type NodeContentReplaceUpdates = {
  [nid: string]: NodeContentUpdate
}
type LeafContentReplaceUpdates = {
  [lid: string]: LeafContentUpdate
}

type TreeContentReplacements = {
  nodes: NodeContentReplaceUpdates
  leaves: LeafContentReplaceUpdates
}

export function getNodeReplacementsForExisting({
  tree,
  existingByName,
  externalUrls,
}: {
  tree: INode
  externalUrls: string[]
  existingByName: Record<string, string>
}): TreeContentReplacements {
  const urlMap = externalUrls.map((existing) => {
    return {
      existing,
      replaceWith: existingByName[existing],
    }
  })
  return getNodeURLReplacements(tree, urlMap)
}

export function getNodeURLReplacements(
  tree: INode,
  urlMap: { existing: string; replaceWith: string }[],
): TreeContentReplacements {
  return urlMap.reduce<TreeContentReplacements>(
    ({ nodes, leaves }, { existing, replaceWith }) => {
      const { nodes: nodesWithContent, leaves: leavesWithContent } =
        nodesAndLeavesWithContent(tree, existing)

      nodesWithContent.forEach((node) => {
        if (!(node.id in nodes)) {
          nodes[node.id] = { node, replacements: [] }
        }
        nodes[node.id].replacements.push({ existing, replaceWith })
      })

      leavesWithContent.forEach((leaf) => {
        if (!(leaf.id in leaves)) {
          leaves[leaf.id] = { leaf, replacements: [] }
        }
        leaves[leaf.id].replacements.push({ existing, replaceWith })
      })

      return { nodes, leaves }
    },
    { nodes: {}, leaves: {} },
  )
}

// This finds all the nodes that contain external urls
// The external urls could be included in leaf data
export function accumulateNodeUpdates(
  replacements: TreeContentReplacements,
): NodeBasedUpdates[] {
  const updates: NodeBasedUpdates[] = []
  Object.values(replacements.nodes).forEach((replacementData) => {
    updates.push({
      node: replacementData.node,
      updates: [
        {
          type: UpdateType.node,
          ids: idsForNode(replacementData.node),
          method: UpdateMethod.update,
          params: getNodeUpdateParams(replacementData),
        },
      ],
    })
  })
  Object.values(replacements.leaves).forEach((replacementData) => {
    updates.push({
      updates: [
        {
          type: UpdateType.leaf,
          ids: idsForNode(replacementData.leaf),
          method: UpdateMethod.update,
          params: getLeafUpdateParams(replacementData),
        },
      ],
    })
  })
  return updates
}

function getNodeUpdateParams({ node, replacements }: NodeContentUpdate) {
  const replacementData = replacements.reduce<Partial<INode>>(
    (acc, { existing, replaceWith }) => {
      const contentTransform = (content: string) => {
        return sanitizeContent(content).replaceAll(existing, replaceWith)
      }
      return replaceParamsForUpdate({
        searchString: existing,
        existingParams: acc,
        contentTransform,
      })
    },
    filterNodeForParams(node),
  )
  return {
    ...replacementData,
    id: node.id,
    tree_id: node.tree_id,
    version: node.version,
  }
}

function getLeafUpdateParams({ leaf, replacements }: LeafContentUpdate) {
  const bodyWithReplacements = replacements.reduce<string>(
    (body, { existing, replaceWith }) => {
      return body.replaceAll(existing, replaceWith)
    },
    JSON.stringify(leaf.body),
  )
  return { body: JSON.parse(bodyWithReplacements) }
}

export function partnerKeyForTree(tree: INode) {
  const attribution = nodeAttribution(tree)!
  return (
    {
      OSE1: 'OpenSciEd',
    }[attribution] || attribution
  )
}

export function subjectForTree(tree: INode) {
  return subjectForCode(getSubjectId(tree))
}

export function sortImageUrls(
  allImageUrls: string[],
  existingUrls: { [key: string]: string },
) {
  return allImageUrls.reduce<{
    canFix: string[]
    missing: string[]
    external: string[]
  }>(
    (acc, url) => {
      if (!isExternalUrl(url)) return acc
      const { canFix, missing, external } = acc
      external.push(url)
      const name = fileName(url)
      if (existingUrls[name]) {
        canFix.push(url)
      } else {
        missing.push(url)
      }
      return { canFix, missing, external }
    },
    { canFix: [], missing: [], external: [] },
  )
}

export function httpAndDataUrls(tree: INode) {
  return allUrlsInTree(tree).reduce<{
    internalUrls: string[]
    externalUrls: string[]
    data: string[]
    malformedMathUrls: string[]
  }>(
    ({ externalUrls, internalUrls, data, malformedMathUrls }, url) => {
      if (isMalformedMathURL(url)) {
        malformedMathUrls.push(url)
      } else if (isDataUrl(url)) {
        data.push(url)
      } else if (isExternalUrl(url)) {
        externalUrls.push(url)
      } else {
        internalUrls.push(url)
      }
      return { externalUrls, internalUrls, data, malformedMathUrls }
    },
    { externalUrls: [], internalUrls: [], data: [], malformedMathUrls: [] },
  )
}

export function dataURItoBlob(dataURI: string) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString
  if (dataURI.split(',')[0].indexOf('base64') >= 0)
    byteString = atob(dataURI.split(',')[1])
  else byteString = unescape(dataURI.split(',')[1])

  // separate out the mime component
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new Blob([ia], { type: mimeString })
}

export function getDataSizeTotal(...dataURI: string[]) {
  return dataURI.reduce<number>((acc, uri) => {
    return acc + dataURItoBlob(uri).size
  }, 0)
}

export function humanFileSize(bytes: number, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  )

  return bytes.toFixed(dp) + ' ' + units[u]
}

export async function dataUrlToFile(
  dataUrl: string,
  fileName: string,
): Promise<File> {
  const res: Response = await fetch(dataUrl)
  const blob: Blob = await res.blob()
  const fileType = dataImageType(dataUrl)
  return new File([blob], fileName, { type: fileType })
}

export async function replaceDataSrcsByS3imgs(dataSrcs: string[], tree: INode) {
  const { promises, nameMap } = dataSrcs.reduce<{
    promises: Promise<File>[]
    nameMap: Record<string, string>
  }>(
    ({ promises, nameMap }, dataSrc) => {
      const generatedName: string = `${nanoid(7)}-imgFromData.${dataImageType(
        dataSrc,
      )}`
      nameMap[generatedName] = dataSrc
      promises.push(dataUrlToFile(dataSrc, generatedName))
      return {
        promises,
        nameMap,
      }
    },
    {
      promises: [],
      nameMap: {},
    },
  )

  const files = await Promise.all(promises)
  const subject = await subjectForTree(tree)
  const ourFiles = await uploadContentFiles(
    {
      partnerKey: partnerKeyForTree(tree),
      subject: subject ? subject.subject_name : '',
      curricula: String(getGradeId(tree)),
      envOverride: 'production',
    },
    ...files,
  )
  const urlMap = Object.entries(ourFiles).map(([key, ourFile]) => {
    return {
      existing: nameMap[key],
      replaceWith: ourFile,
    }
  })
  const replacements = getNodeURLReplacements(tree, urlMap)
  const updates = accumulateNodeUpdates(replacements)
  return updateAll(...updates)
}

export async function verifyMimeTypes(...urls: string[]): Promise<string[]> {
  const withIssue: string[] = []
  const doUrl = async (url: string) => {
    const result = await fetch(url, { method: GET }).catch(() =>
      Promise.resolve(null),
    )
    if (!result) return
    const existingMimeType = result.headers.get('content-type') ?? ''
    const correctMimeType = mime.getType(url)
    if (existingMimeType.startsWith('binary') && correctMimeType) {
      withIssue.push(url)
    }
  }
  await doInBatches(urls, doUrl)
  return withIssue
}
